import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { InstalledUpiApp } from '../../utils/installedUpiIntents'
import { getBottomPadding, getTopPadding } from '../../utils/sessionStorage'
import { upiIcon } from '../../utils/mf-execution/paymentPageConfig'
import RenderHTML from '../../components/ui-components/RenderHTML'
import BackButton from '../../components/ui-components/BackButton'
import { PrimaryButton } from '../../components/execution/PrimaryButton'
import {
  InvestPaymentMethodType,
  InvestPaymentMethodTypes,
  PaymentPageType,
} from '../../types/mf-execution.types'
import { convertNumberToCurrency } from '../../utils/utils'
import { useBottomSheet } from '../../state-management/contexts/BottomSheetContext'
import { SelectBankAccountBottomSheet } from '../../components/BottomSheetComponents/mf-execution/SelectBankAccountBottomSheet'
import {
  applyBankAccountToUpiMethod,
  buildUpiIntentForSelection,
  filterInstalledUpiAppsForPayment,
  getDefaultInstalledUpiApp,
  getInitialSelectedBankAccount,
  getNetBankingSelectionId,
  getUpiAppSelectionId,
  isAmountWithinUpiIntentLimit,
  isConfirmCtaEnabled,
  isUpiIntentPaymentMethod,
  normalizePaymentMethodForOrder,
  resolveConfirmablePaymentMethod,
} from '../../utils/mf-execution/paymentMethodSelection'
import {
  getBankLogoSrc,
  getLinkedBankAccounts,
  getSelectionIdFromPaymentMethod,
} from '../../utils/mf-execution/paymentMethodsScreenUtils'
import { PaymentMethodExpandableSection } from '../../components/execution/payment-methods/PaymentMethodExpandableSection'
import { PaymentMethodItem } from '../../components/execution/payment-methods/PaymentMethodItem'
import { PaymentMethodSelectableRow } from '../../components/execution/payment-methods/PaymentMethodSelectableRow'
import { UpiLinkedBankDetails } from '../../components/execution/payment-methods/UpiLinkedBankDetails'

export type PaymentMethodsScreenProps = {
  amount: number
  isin?: string
  paymentPageType?: PaymentPageType
  upiIntentMaximumAllowedAmount?: number
  paymentMethods: InvestPaymentMethodType[] | undefined
  bankAccountDetails?: InvestPaymentMethodType[]
  defaultPaymentMethodDetails?: InvestPaymentMethodType | null
  selectedPaymentMethod: InvestPaymentMethodType | null
  onPaymentMethodChange: (paymentMethod: InvestPaymentMethodType | null) => void
  onRefetchPaymentMethods?: () => void
  installedUpiApps: InstalledUpiApp[] | null
  requestUpiIntents: () => void
  /** Fired after the user confirms a payment method (e.g. analytics). */
  onConfirm?: (paymentMethod: InvestPaymentMethodType) => void
  /** When true, Confirm stays disabled (e.g. SIP missing calendar selection). */
  isConfirmBlocked?: boolean
}

/**
 * Full-screen payment method picker for buy/SIP flows.
 * UI building blocks live in PaymentMethodsScreenComponents; helpers in paymentMethodsScreenUtils.
 * Autopay v2 reuses those components via AutopayMethodsScreen with mandate-details data.
 */
export function PaymentMethodsScreen({
  amount,
  upiIntentMaximumAllowedAmount = 0,
  paymentMethods,
  bankAccountDetails,
  defaultPaymentMethodDetails,
  selectedPaymentMethod,
  onPaymentMethodChange,
  installedUpiApps,
  requestUpiIntents,
  onConfirm,
  isConfirmBlocked = false,
}: PaymentMethodsScreenProps) {
  const navigate = useNavigate()
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const eligibleInstalledUpiApps = useMemo(
    () => filterInstalledUpiAppsForPayment(installedUpiApps),
    [installedUpiApps]
  )
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(() =>
    getSelectionIdFromPaymentMethod(selectedPaymentMethod, eligibleInstalledUpiApps)
  )
  const [selectedBankAccount, setSelectedBankAccount] = useState<InvestPaymentMethodType | null>(() =>
    getInitialSelectedBankAccount(bankAccountDetails, defaultPaymentMethodDetails)
  )
  const [payablePaymentMethod, setPayablePaymentMethod] = useState<InvestPaymentMethodType | null>(() =>
    selectedPaymentMethod?.paymentMethodId ? normalizePaymentMethodForOrder(selectedPaymentMethod) : null
  )
  const hasAutoSelectedDefaultUpiRef = useRef(false)

  const isUpiIntentOverLimit =
    amount > 0 &&
    (upiIntentMaximumAllowedAmount ?? 0) > 0 &&
    !isAmountWithinUpiIntentLimit(amount, upiIntentMaximumAllowedAmount)

  const netBankingMethods =
    paymentMethods?.filter((method) => method?.type === InvestPaymentMethodTypes.NET_BANKING) ?? []

  const hasInstalledUpiIntents = eligibleInstalledUpiApps.length > 0

  const upiLimitLabel =
    (upiIntentMaximumAllowedAmount ?? 0) > 0
      ? convertNumberToCurrency({ amount: upiIntentMaximumAllowedAmount, isRound: true, casing: 'PASCAL' })
      : ''

  useEffect(() => {
    requestUpiIntents()
  }, [requestUpiIntents])

  useEffect(() => {
    if (selectedPaymentMethod?.type !== InvestPaymentMethodTypes.NET_BANKING) return
    setPayablePaymentMethod(selectedPaymentMethod)
    setSelectedOptionId(getSelectionIdFromPaymentMethod(selectedPaymentMethod, eligibleInstalledUpiApps))
  }, [selectedPaymentMethod, eligibleInstalledUpiApps])

  useEffect(() => {
    if (!bankAccountDetails?.length) return

    setSelectedBankAccount((current) => {
      if (
        current?.paymentMethodId &&
        bankAccountDetails.some((bank) => bank?.paymentMethodId === current.paymentMethodId)
      ) {
        return current
      }
      return getInitialSelectedBankAccount(bankAccountDetails, defaultPaymentMethodDetails)
    })
  }, [bankAccountDetails, defaultPaymentMethodDetails])

  const updatePendingSelection = (method: InvestPaymentMethodType | null) => {
    if (!method?.paymentMethodId) return
    setPayablePaymentMethod(normalizePaymentMethodForOrder(method))
  }

  const handleBankAccountConfirm = (bankAccount: InvestPaymentMethodType) => {
    setSelectedBankAccount(bankAccount)

    const activeMethod = payablePaymentMethod ?? selectedPaymentMethod
    if (!activeMethod) return

    const isUpiMethod =
      activeMethod?.type === InvestPaymentMethodTypes.UPI ||
      activeMethod?.type === InvestPaymentMethodTypes.UPI_INTENT

    if (!isUpiMethod) return

    updatePendingSelection(
      applyBankAccountToUpiMethod(activeMethod, bankAccount, activeMethod?.upiPackageName ?? null)
    )
  }

  const openSelectBankAccountBottomSheet = () => {
    const accounts = bankAccountDetails ?? []
    if (accounts.length <= 1) return

    openBottomSheet(
      <SelectBankAccountBottomSheet
        ScreenName='PaymentMethodsScreen'
        bankAccounts={accounts}
        selectedBankAccount={selectedBankAccount}
        onDismiss={closeBottomSheet}
        onConfirm={handleBankAccountConfirm}
      />
    )
  }

  const syncUpiIntentSelection = (app: InstalledUpiApp) => {
    const selectionId = getUpiAppSelectionId(app)
    const bankAccount =
      selectedBankAccount ?? getInitialSelectedBankAccount(bankAccountDetails, defaultPaymentMethodDetails)
    const upiIntentMethod = buildUpiIntentForSelection(
      selectionId,
      eligibleInstalledUpiApps,
      paymentMethods,
      bankAccount,
      bankAccountDetails,
      defaultPaymentMethodDetails
    )

    if (!upiIntentMethod?.paymentMethodId) return

    if (bankAccount && !selectedBankAccount) {
      setSelectedBankAccount(bankAccount)
    }

    setSelectedOptionId(selectionId)
    updatePendingSelection(upiIntentMethod)
  }

  const handleUpiAppSelect = (app: InstalledUpiApp) => {
    if (isUpiIntentOverLimit) return
    syncUpiIntentSelection(app)
  }

  const handleNetBankingSelect = (method: InvestPaymentMethodType) => {
    setSelectedOptionId(getNetBankingSelectionId(method))
    updatePendingSelection(method)
  }

  const confirmResolutionParams = {
    payablePaymentMethod,
    selectedOptionId,
    selectedBankAccount,
    installedUpiApps: eligibleInstalledUpiApps,
    paymentMethods,
    bankAccountDetails,
    defaultPaymentMethodDetails,
  }

  const handleConfirmClick = () => {
    const methodToConfirm = resolveConfirmablePaymentMethod(confirmResolutionParams)
    if (!methodToConfirm?.paymentMethodId) return
    const normalizedMethod = normalizePaymentMethodForOrder(methodToConfirm)
    onPaymentMethodChange(normalizedMethod)
    onConfirm?.(normalizedMethod)
    navigate(-1)
  }

  useEffect(() => {
    if (hasInstalledUpiIntents && !isUpiIntentOverLimit) return
    if (!netBankingMethods.length) return

    const firstNetBanking = netBankingMethods[0]
    if (!firstNetBanking?.paymentMethodId) return

    const netBankingSelectionId = getNetBankingSelectionId(firstNetBanking)
    if (
      selectedOptionId === netBankingSelectionId &&
      payablePaymentMethod?.type === InvestPaymentMethodTypes.NET_BANKING
    ) {
      return
    }

    setSelectedOptionId(netBankingSelectionId)
    updatePendingSelection(firstNetBanking)
  }, [
    hasInstalledUpiIntents,
    isUpiIntentOverLimit,
    amount,
    netBankingMethods,
    selectedOptionId,
    payablePaymentMethod?.type,
    payablePaymentMethod?.paymentMethodId,
  ])

  useEffect(() => {
    if (isUpiIntentOverLimit) return
    if (!selectedOptionId?.startsWith('upi:') || !eligibleInstalledUpiApps.length) return

    const selectedApp = eligibleInstalledUpiApps.find((app) => getUpiAppSelectionId(app) === selectedOptionId)
    if (!selectedApp) return

    const bankAccount =
      selectedBankAccount ?? getInitialSelectedBankAccount(bankAccountDetails, defaultPaymentMethodDetails)
    const upiIntentMethod = buildUpiIntentForSelection(
      selectedOptionId,
      eligibleInstalledUpiApps,
      paymentMethods,
      bankAccount,
      bankAccountDetails,
      defaultPaymentMethodDetails
    )

    if (!upiIntentMethod?.paymentMethodId) return

    const needsSync =
      payablePaymentMethod?.type !== InvestPaymentMethodTypes.UPI_INTENT ||
      payablePaymentMethod?.upiPackageName !== upiIntentMethod?.upiPackageName ||
      payablePaymentMethod?.paymentMethodId !== upiIntentMethod?.paymentMethodId

    if (needsSync) {
      updatePendingSelection(upiIntentMethod)
    }
  }, [
    isUpiIntentOverLimit,
    bankAccountDetails,
    selectedOptionId,
    paymentMethods,
    eligibleInstalledUpiApps,
    selectedBankAccount,
    defaultPaymentMethodDetails,
  ])

  useEffect(() => {
    if (isUpiIntentOverLimit) return
    if (hasAutoSelectedDefaultUpiRef.current || !eligibleInstalledUpiApps.length || !paymentMethods?.length) return
    if (payablePaymentMethod?.upiPackageName || selectedOptionId) return
    if (selectedPaymentMethod?.type === InvestPaymentMethodTypes.NET_BANKING) return
    if (selectedPaymentMethod?.type === InvestPaymentMethodTypes.MANDATE) return
    if (isUpiIntentPaymentMethod(selectedPaymentMethod)) return

    const defaultApp = getDefaultInstalledUpiApp(eligibleInstalledUpiApps, defaultPaymentMethodDetails)

    if (defaultApp) {
      hasAutoSelectedDefaultUpiRef.current = true
      syncUpiIntentSelection(defaultApp)
    }
  }, [
    isUpiIntentOverLimit,
    eligibleInstalledUpiApps,
    paymentMethods,
    bankAccountDetails,
    defaultPaymentMethodDetails,
    selectedOptionId,
    selectedPaymentMethod,
  ])

  const isConfirmDisabled = isConfirmBlocked || !isConfirmCtaEnabled(confirmResolutionParams)

  const netBankingSection = (
    <div className={hasInstalledUpiIntents && !isUpiIntentOverLimit ? 'pt-5' : ''}>
      <p className='text-sm text-grey40 justify-start px-5 mb-4'>Net banking</p>
      <div>
        {netBankingMethods.map((method, index, methods) => (
          <PaymentMethodSelectableRow
            key={`${method?.paymentMethodId ?? method?.displayAccNumber ?? index}`}
            showBorderBelow={index < methods.length - 1}
          >
            <PaymentMethodItem
              imageSrc={getBankLogoSrc(method?.logoUrl)}
              imageAlt={method?.displayAccNumber ?? 'Net banking accounts'}
              title={method?.displayAccNumber ?? ''}
              imageSizeClassName='w-10 h-10'
              isSelected={selectedOptionId === getNetBankingSelectionId(method)}
              onSelect={() => handleNetBankingSelect(method)}
            />
          </PaymentMethodSelectableRow>
        ))}
      </div>
    </div>
  )

  const upiSection = (
    <>
      <div className='flex flex-row items-center gap-2 w-full px-5 mt-4'>
        <p className='text-sm text-grey40 justify-start'>Pay via</p>
        <RenderHTML innerHTMLText={upiIcon} />
      </div>
      {isUpiIntentOverLimit && upiLimitLabel ? (
        <p className='text-[13px] font-[400] text-[#E77464] px-5 mb-5'>
          UPI payments are limited to {upiLimitLabel}
        </p>
      ) : null}
      <ul className='flex flex-col'>
        {eligibleInstalledUpiApps.map((app, index, apps) => {
          const isUpiSelected =
            !isUpiIntentOverLimit && selectedOptionId === getUpiAppSelectionId(app)
          const linkedBankAccounts = isUpiSelected
            ? getLinkedBankAccounts(bankAccountDetails, app, paymentMethods)
            : []

          return (
            <li
              key={`${app.packageName ?? app.name}-${index}`}
              className={index < apps.length - 1 ? 'border-b border-grey60' : ''}
            >
              <PaymentMethodSelectableRow>
                <PaymentMethodItem
                  imageSrc={app?.appLogoUrl}
                  imageAlt={app?.name ?? ''}
                  title={app?.name ?? ''}
                  imageSizeClassName='w-10 h-10'
                  isSelected={isUpiSelected}
                  isDisabled={isUpiIntentOverLimit}
                  onSelect={() => handleUpiAppSelect(app)}
                />
                <PaymentMethodExpandableSection
                  isExpanded={isUpiSelected && linkedBankAccounts.length > 0}
                >
                  <UpiLinkedBankDetails
                    bankAccounts={linkedBankAccounts}
                    selectedBankAccount={selectedBankAccount}
                    onSelectBankClick={
                      linkedBankAccounts.length > 1 ? openSelectBankAccountBottomSheet : undefined
                    }
                  />
                </PaymentMethodExpandableSection>
              </PaymentMethodSelectableRow>
            </li>
          )
        })}
      </ul>
    </>
  )

  return (
    <div
      className='h-screen overflow-y-auto no-scrollbar overscroll-none bg-white'
      style={{ paddingBottom: `calc(${getBottomPadding()}px + 16px)` }}
    >
      <div
        className='sticky top-0 z-30 bg-white px-5 pb-4'
        style={{ paddingTop: `calc(${getTopPadding()}px + 8px)` }}
      >
        <div className='flex items-center justify-between w-full'>
          <BackButton size={24} onClick={() => navigate(-1)} />
          <div className='text-[16px] font-[500] text-[#232323]'>Select payment method</div>
          <div className='w-9' />
        </div>
      </div>
      <div className='pb-24'>
        {isUpiIntentOverLimit ? (
          <>
            {netBankingSection}
            {hasInstalledUpiIntents ? (
              <>
                <div className='bg-grey80 h-4' />
                {upiSection}
              </>
            ) : null}
          </>
        ) : (
          <>
            {hasInstalledUpiIntents ? upiSection : null}
            {hasInstalledUpiIntents && netBankingMethods.length > 0 ? (
              <div className='bg-grey80 h-4' />
            ) : null}
            {netBankingSection}
          </>
        )}
      </div>
      <div
        className='absolute bottom-0 left-0 right-0 z-20 bg-white p-5'
        style={{ paddingBottom: `calc(${getBottomPadding()}px + 16px)` }}
      >
        <PrimaryButton
          onClick={handleConfirmClick}
          label='Confirm'
          isDisabled={isConfirmDisabled}
        />
      </div>
    </div>
  )
}
